import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import useProject from '../hooks/useProject';
import useIntegration from '../hooks/useIntegration';
import IntegrationForm from '../components/integrations/IntegrationForm';
import { formatDateTime } from '../utils/dateUtils';

const Integrations = () => {
  const { projectId } = useParams();
  const { project: projectData, refetchProject } = useProject({ projectId });
  const { getProjectIntegrations, disconnectExternalSystem, loading, error } = useIntegration();
  
  const [project, setProject] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loadingProject, setLoadingProject] = useState(true);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingProject(true);
        
        // Load project details
        await refetchProject();
        setProject(projectData);
        
        // Load integrations
        const integrationData = await getProjectIntegrations(projectId);
        setIntegrations(integrationData || []);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoadingProject(false);
      }
    };
    
    loadData();
  }, [projectId, refetchProject, projectData, getProjectIntegrations]);
  
  const handleAddIntegration = (newIntegration) => {
    setIntegrations([...integrations, newIntegration]);
    setShowForm(false);
  };
  
  const handleDisconnect = async (integrationId) => {
    try {
      await disconnectExternalSystem(integrationId);
      setIntegrations(integrations.filter(i => i._id !== integrationId));
    } catch (err) {
      console.error('Error disconnecting integration:', err);
    }
  };
  
  if (loadingProject || loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            component={Link}
            to={`/projects/${projectId}`}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            Back to Project
          </Button>
          
          <Box className="page-title-container">
            <Box>
              <Typography variant="h5" component="h1" fontWeight={600}>
                {project?.name} - Integrations
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Connect and manage external integrations for this project.
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            Add Integration
          </Button>
        </Box>
        
        {showForm && (
          <Box sx={{ mb: 4 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Add New Integration
              </Typography>
              <IntegrationForm 
                projectId={projectId}
                onSuccess={handleAddIntegration}
                onCancel={() => setShowForm(false)}
              />
            </Paper>
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <List>
            {integrations.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No integrations found"
                  secondary="Add an integration to sync with external systems"
                />
              </ListItem>
            ) : (
              integrations.map((integration, index) => (
                <React.Fragment key={integration._id || integration.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon>
                      <LinkIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={integration.integrationType}
                      secondary={`Status: ${integration.status} • Last synced: ${integration.lastSyncedAt ? formatDateTime(integration.lastSyncedAt) : 'Never'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="sync"
                        sx={{ mr: 1 }}
                      >
                        <SyncIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="delete" 
                        onClick={() => handleDisconnect(integration._id || integration.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default Integrations;